import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { CalendarCheck, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtTime(iso, tz) {
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz });
  } catch { return iso; }
}
function fmtTimeWithZone(iso, tz) {
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      hour: "numeric", minute: "2-digit", timeZone: tz, timeZoneName: "short",
    });
  } catch { return iso; }
}
function fmtDateTimeWithZone(iso, tz) {
  try {
    return new Date(iso).toLocaleString("en-US", {
      weekday: "long", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
      timeZone: tz, timeZoneName: "short",
    });
  } catch { return iso; }
}
// Produce YYYY-MM-DD for a given instant *in the tenant's timezone*
function toDateInputInTZ(d, tz) {
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(d);
    const y = parts.find(p => p.type === "year").value;
    const m = parts.find(p => p.type === "month").value;
    const dd = parts.find(p => p.type === "day").value;
    return `${y}-${m}-${dd}`;
  } catch {
    const y = d.getFullYear(); const m = String(d.getMonth()+1).padStart(2,"0"); const dd = String(d.getDate()).padStart(2,"0");
    return `${y}-${m}-${dd}`;
  }
}

export default function Book() {
  const { leadId } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [day, setDay] = useState(() => { const d = new Date(); d.setDate(d.getDate()+1); return d; });
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/leads/${leadId}/booking-info`)
      .then(r => setInfo(r.data))
      .catch(() => setError("This booking link is invalid or has expired."));
  }, [leadId]);

  const loadSlots = async (d) => {
    setSlotsLoading(true); setSlots([]); setPicked(null);
    try {
      const tz = info?.scheduling?.timezone;
      const r = await axios.get(`${API}/public/leads/${leadId}/availability`, { params: { date: toDateInputInTZ(d, tz) } });
      setSlots(r.data.slots || []);
    } catch { setSlots([]); }
    setSlotsLoading(false);
  };

  useEffect(() => { if (info && !info.already_booked) loadSlots(day); /* eslint-disable-next-line */ }, [info, day]);

  const book = async () => {
    if (!picked) return;
    setBooking(true);
    try {
      const r = await axios.post(`${API}/public/leads/${leadId}/book`, { scheduled_at: picked.start });
      setConfirmed(r.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Booking failed — please try another time.");
      loadSlots(day);
    }
    setBooking(false);
  };

  const prettyDay = useMemo(() => {
    const tz = info?.scheduling?.timezone;
    return day.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: tz });
  }, [day, info]);
  const shiftDay = (n) => { const d = new Date(day); d.setDate(d.getDate()+n); setDay(d); };

  if (error) return (
    <Shell><p className="text-center text-slate-400" data-testid="book-error">{error}</p></Shell>
  );
  if (!info) return <Shell><p className="text-center text-slate-400">Loading…</p></Shell>;

  if (confirmed || info.already_booked) {
    const when = confirmed?.scheduled_at || null;
    return (
      <Shell>
        <div className="text-center" data-testid="book-confirmed">
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-400 mb-4" />
          <h1 className="font-display text-3xl font-bold mb-2">You're booked!</h1>
          <p className="text-slate-300 text-lg">{info.org_name} will see you</p>
          {when && (
            <p className="font-mono text-orange-400 text-xl mt-2" data-testid="book-confirmed-time">
              {fmtDateTimeWithZone(when, info.scheduling.timezone)}
            </p>
          )}
          {!when && info.already_booked && (
            <p className="text-slate-400 mt-2">An estimate is already scheduled for this lead.</p>
          )}
          <p className="text-slate-500 text-sm mt-6">We'll text you a reminder before the visit. Reply STOP any time to opt out.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <header className="text-center mb-6">
        <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-2">{info.org_name}</p>
        <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Pick a time for your free roof estimate</h1>
        <p className="text-slate-400 mt-2 text-sm">Hi {info.caller_name} — estimates take about {info.scheduling.estimate_duration_minutes} minutes.</p>
      </header>

      <Card className="bg-slate-900 border-slate-800 p-6">
        <div className="flex items-center justify-between mb-5" data-testid="book-day-nav">
          <Button variant="ghost" onClick={() => shiftDay(-1)} data-testid="book-prev-day" className="text-slate-300">
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <div className="text-center">
            <p className="font-display text-lg font-semibold">{prettyDay}</p>
            <p className="text-xs text-slate-500 font-mono" data-testid="book-tz-label">All times in {info.scheduling.timezone}</p>
          </div>
          <Button variant="ghost" onClick={() => shiftDay(1)} data-testid="book-next-day" className="text-slate-300">
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {slotsLoading && <p className="text-center text-slate-500 text-sm py-8" data-testid="book-slots-loading">Finding available times…</p>}
        {!slotsLoading && slots.length === 0 && (
          <p className="text-center text-slate-500 text-sm py-8" data-testid="book-no-slots">
            No slots available on this day. Try another day using the arrows above.
          </p>
        )}
        {!slotsLoading && slots.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="book-slot-grid">
            {slots.map((s) => {
              const active = picked?.start === s.start;
              return (
                <button
                  key={s.start}
                  disabled={!s.available}
                  onClick={() => setPicked(s)}
                  data-testid={`slot-${s.start}`}
                  className={`p-3 rounded-lg font-mono text-sm border transition-colors ${
                    !s.available
                      ? "bg-slate-800/30 border-slate-800 text-slate-600 line-through cursor-not-allowed"
                      : active
                      ? "bg-orange-500 border-orange-400 text-slate-900 font-bold"
                      : "bg-slate-800 border-slate-700 text-slate-200 hover:border-orange-500/60"
                  }`}
                >
                  {fmtTimeWithZone(s.start, info.scheduling.timezone)}
                </button>
              );
            })}
          </div>
        )}

        <div className="pt-6 mt-6 border-t border-slate-800">
          <Button
            onClick={book}
            disabled={!picked || booking}
            data-testid="book-submit"
            className="w-full bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold text-base h-12"
          >
            <CalendarCheck className="w-5 h-5 mr-2" />
            {booking ? "Booking…" : picked ? `Confirm ${fmtTimeWithZone(picked.start, info.scheduling.timezone)}` : "Pick a time above"}
          </Button>
        </div>
      </Card>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100">
      <header className="h-16 border-b border-slate-800 flex items-center px-6"><Logo /></header>
      <main className="max-w-2xl mx-auto p-6 lg:p-10">{children}</main>
      <footer className="text-center text-xs text-slate-600 py-6 space-x-3">
        <Link to="/privacy" className="hover:text-slate-400">Privacy</Link>
        <span>·</span>
        <Link to="/terms" className="hover:text-slate-400">Terms</Link>
      </footer>
    </div>
  );
}
